import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSession } from 'next-auth/react'

import { getAll, ResponseAllInfo } from '#/entities/user-account/model/user-type-slice'
import { accountApi } from '#/shared/api/account-endpoints'
import { useShowDataStore } from '#/shared/lib/hooks/use-show-data'

type ApiKeyTokenLimit = string | number | null

export type ApiKeyRaw = {
	key: string
	name: string
	token_limit: ApiKeyTokenLimit
	created_at: string
	expires_at: string | null
}

export type ApiKeyView = {
	id: string
	name: string
	keyValue: string
	limit: ApiKeyTokenLimit
	createdAt: string
	expiresAt: string | null
}

export const useApiKeysPage = () => {
	const [keys, setKeys] = useState<ApiKeyRaw[] | null>(null)
	const [loading, setLoading] = useState(false)
	const [modalOpen, setModalOpen] = useState(false)
	const [userInfo, setUserInfo] = useState<ResponseAllInfo | null>()
	const [disabled, setDisabled] = useState(true)

	const { data, status } = useSession()
	const { showMessage } = useShowDataStore()

	useEffect(() => {
		getAll(data?.access).then((res) => setUserInfo(res))
	}, [data?.access])

	const fetchKeys = useCallback(async () => {
		if (status !== 'authenticated') {
			return
		}

		setLoading(true)

		const response = await accountApi.getApiKeys(data?.access)
		
		if (response.status >= 200 && response.status < 300) {
			setKeys(response.data)
		} else if (response.status >= 400 && response.data?.detail) {
			showMessage(response.data.detail)
		} else {
			showMessage('Ошибка при получении API ключей')
		}

		setLoading(false)
	}, [data?.access, status, showMessage])

	useEffect(() => {
		if (status === 'authenticated') {
			fetchKeys()
		}
	}, [fetchKeys, status])

	useEffect(() => {
		if (
			userInfo?.account_type === 'business_security' ||
			userInfo?.account_type === 'business_account' ||
			status !== 'authenticated' ||
			loading
		) {
			setDisabled(true)
		} else {
			setDisabled(false)
		}
	}, [status, userInfo, loading])

	const deleteKey = useCallback(
		async (name: string) => {
			if (status !== 'authenticated') {
				return
			}

			setLoading(true)

			const response = await accountApi.deleteApiKey(name, data?.access)

			if (response.status >= 200 && response.status < 300) {
				await fetchKeys()
			} else if (response.status >= 400 && response.data?.detail) {
				showMessage(response.data.detail)
			} else {
				showMessage('Ошибка при удалении API ключа')
			}

			setLoading(false)
		},
		[data?.access, fetchKeys, status, showMessage]
	)

	const keyItems = useMemo<ApiKeyView[]>(
		() =>
			keys?.map((item) => ({
				id: item.key,
				name: item.name,
				keyValue: item.key,
				limit: item.token_limit,
				createdAt: item.created_at.split('T')[0],
				expiresAt: item.expires_at,
			})) ?? [],
		[keys]
	)

	const hasKeys = keyItems.length > 0

	return {
		keys,
		setKeys,
		keyItems,
		hasKeys,
		loading,
		modalOpen,
		setModalOpen,
		disabled,
		deleteKey,
	}
}

export type ApiKeysPageState = ReturnType<typeof useApiKeysPage>

